import { type UseQueryResult, useQueries } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import type { IGoodsReceivingService } from '../../../services/goods-receiving';
import { GoodsReceivingService } from '../../../services/goods-receiving';
import { goodsReceivingQueryKeys } from '../query-keys';

interface UsePresignedUrlsResult {
  photoUrls: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to fetch presigned URLs for multiple photos in parallel.
 * Uses TanStack Query for caching and deduplication.
 */
export function usePresignedUrls(
  photos: Array<{ id: string; storageKey: string }>,
): UsePresignedUrlsResult {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);

  const results: UseQueryResult<string>[] = useQueries({
    queries: photos.map((photo) => ({
      queryKey: goodsReceivingQueryKeys.presignedUrl(photo.storageKey),
      queryFn: () => service.getPresignedUrl(photo.storageKey),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const photoUrls = new Map<string, string>();
  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  results.forEach((result, index) => {
    if (result.data) {
      photoUrls.set(photos[index].id, result.data);
    }
  });

  return { photoUrls, isLoading, isError };
}
